import { useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { MarketingContactTable } from '../shared/MarketingContactTable'
import { Stats } from '../Stats'
import AttributionSelect from './AttributionSelect'
import { FilterOperator } from 'app/filter.utils'
import { showModal } from 'app/lib/modal'
import { defaultBecameLeadDateFilter } from 'app/utils/defaultBecameLeadDateFilter'
import type { AttributionSummaryType } from 'app/types/attribution'
import { defaultColumns } from 'app/table-columns/attribution-contacts'
import { useHandleSearchParams } from 'app/utils'
import { assertGood } from 'lib/assertBoom'

const REST_LABEL = `Other`
const COLORS = [`#3B82F6`, `#10B981`, `#FDBA74`, `#F87171`, `#E5E5E5`]

const filterToKey = (filter: `Leads` | `Qualified Leads` | `Appointments Scheduled` | `Patients`) => {
  switch (filter) {
    case `Leads`:
      return `leads`
    case `Qualified Leads`:
      return `mqls`
    case `Appointments Scheduled`:
      return `appointments`
    case `Patients`:
      return `patients`
  }
}

export default function AttributionCard({
  type,
  data,
  fromDate,
  toDate,
  locationIds,
  integrationStatus,
}: {
  type: `sources` | `mediums` | `campaigns`
  data?: AttributionSummaryType
  fromDate: string
  toDate: string
  locationIds: string[]
  integrationStatus?: string
}) {
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const [utmToFilterBy, setUtmToFilterBy] = useState<string | null>()
  const [filter, setFilter] = useState<`Leads` | `Qualified Leads` | `Appointments Scheduled` | `Patients`>(`Leads`)
  const [topFive, setTopFive] = useState<{ name: string; count: number }[]>([])
  const [rest, setRest] = useState<{ name: string; count: number }[]>([])
  const [restCount, setRestCount] = useState<number>()
  // normalize
  const total = useMemo(() => data?.[filterToKey(filter)].count ?? 0, [data, filter])
  const normalized = useMemo(() => {
    if (!total) return []
    const topFiveNormalized = topFive
      .map((item) => ({ ...item, normalized: (item.count / total) * 100 }))
      .sort((a, b) => b.count - a.count)
    if (restCount) {
      topFiveNormalized.push({ name: REST_LABEL, count: restCount, normalized: (restCount / total) * 100 })
    }
    return topFiveNormalized
  }, [topFive, total])

  const handleFilterChange = (filter: `Leads` | `Qualified Leads` | `Appointments Scheduled` | `Patients`) => {
    setFilter(filter)
  }

  const calculateTopFiveAndTotal = (contactType: keyof AttributionSummaryType) => {
    assertGood(data)
    const sorted = [...data[contactType][type]].sort((a, b) => b.count - a.count)
    const topFive = sorted.slice(0, 5)
    setTopFive(topFive)
    const rest = sorted.slice(5)
    setRest(rest)
    const count = sorted.slice(5).reduce((acc, current) => acc + current.count, 0)
    setRestCount(count)
  }

  const handleCloseModal = () => setUtmToFilterBy(null)

  useEffect(() => {
    if (searchParams.has(`${type}Details`)) {
      setUtmToFilterBy(searchParams.get(`${type}Details`)!)
    }
    if (searchParams.has(`${type}Filter`)) {
      setFilter(searchParams.get(`${type}Filter`) as any)
    }
  }, [])

  useEffect(() => {
    if (!data) return
    calculateTopFiveAndTotal(filterToKey(filter))
  }, [data, filter])

  useEffect(() => {
    let currentParam = searchParams.get(`${type}Details`)
    if (utmToFilterBy === currentParam) return
    handleSearchParams({
      [`${type}Details`]: utmToFilterBy ?? undefined,
      ...(searchParams.has(`filters`) ? { filters: undefined } : undefined),
    })
  }, [utmToFilterBy])

  useEffect(() => {
    let currentParam = searchParams.get(`${type}Filter`)
    if (filter === currentParam) return
    handleSearchParams({
      [`${type}Filter`]: filter ?? undefined,
      ...(searchParams.has(`filters`) ? { filters: undefined } : undefined),
    })
  }, [filter])

  useEffect(() => {
    if (utmToFilterBy && fromDate && toDate) {
      const filterBy = filter === `Appointments Scheduled` ? `Appointments` : filter
      const defaultFilterKey = type === `sources` ? `utmSource` : type === `mediums` ? `utmMedium` : `utmCampaign`
      const defaultFilter = {
        [defaultFilterKey]:
          utmToFilterBy === REST_LABEL
            ? {
                operator: `not_in` as FilterOperator,
                value: normalized.map((item) => item.name).filter((name) => name !== REST_LABEL),
                readOnly: true,
              }
            : {
                operator: `exactly` as FilterOperator,
                value: utmToFilterBy,
                readOnly: true,
              },
        ...(searchParams.has(`fromDate`) && searchParams.has(`toDate`)
          ? {
              [`becameLeadDate`]: {
                operator: `between` as FilterOperator,
                value: [searchParams.get(`fromDate`)!, searchParams.get(`toDate`)!],
                readOnly: true,
              },
            }
          : defaultBecameLeadDateFilter),
        ...(filterBy !== `Leads` && {
          [`isQualified`]: {
            operator: `exactly` as FilterOperator,
            value: `true`,
            readOnly: true,
          },
        }),
        ...(filterBy === `Appointments` && {
          [`firstAppointmentMadeDate`]: {
            operator: `attribute_exists` as FilterOperator,
            readOnly: true,
          },
        }),
        ...(filterBy === `Patients` && {
          [`becamePatientDate`]: {
            operator: `attribute_exists` as FilterOperator,
            readOnly: true,
          },
        }),
      }
      showModal(
        <MarketingContactTable
          title={utmToFilterBy}
          filterContactsBy={filterBy}
          fromDate={fromDate}
          toDate={toDate}
          locationIds={locationIds}
          integrationStatus={integrationStatus}
          showDisqualified={filterBy === `Leads` ? true : false}
          utmSource={
            type === `sources`
              ? utmToFilterBy === REST_LABEL
                ? `NOT (${normalized
                    .map((item) => item.name)
                    .filter((name) => name !== REST_LABEL)
                    .map((item) => `utmSource.keyword:"${item}"`)
                    .join(` OR `)})`
                : `utmSource.keyword:"${utmToFilterBy}"`
              : undefined
          }
          utmMedium={
            type === `mediums`
              ? utmToFilterBy === REST_LABEL
                ? `NOT (${normalized
                    .map((item) => item.name)
                    .filter((name) => name !== REST_LABEL)
                    .map((item) => `utmMedium.keyword:"${item}"`)
                    .join(` OR `)})`
                : `utmMedium.keyword:"${utmToFilterBy}"`
              : undefined
          }
          utmCampaign={
            type === `campaigns`
              ? utmToFilterBy === REST_LABEL
                ? `NOT (${normalized
                    .map((item) => item.name)
                    .filter((name) => name !== REST_LABEL)
                    .map((item) => `utmCampaign.keyword:"${item}"`)
                    .join(` OR `)})`
                : `utmCampaign.keyword:"${utmToFilterBy}"`
              : undefined
          }
          defaultFilters={defaultFilter}
          customColumns={defaultColumns}
          onClose={handleCloseModal}
        />,
        {
          hideTitle: true,
          removePadding: true,
          isFullScreen: true,
          onClose: handleCloseModal,
          useRadix: true,
        },
      )
    }
  }, [utmToFilterBy, fromDate, toDate])

  const openModal = (value: string) => {
    setUtmToFilterBy(value)
  }

  return (
    <div className="border-default flex h-[489px] w-full flex-col items-start rounded-lg border p-5 2xl:w-auto 2xl:flex-1">
      <div className="flex w-full items-start justify-between">
        <div className="flex flex-col gap-2">
          <p className="text-ui-md capitalize !leading-6">Top 5 {type}</p>
          <p className="text-strong text-heading-lg">{total?.toLocaleString(`en-US`)}</p>
        </div>
        <AttributionSelect defaultFilter={filter} onFilterChange={handleFilterChange} />
      </div>
      <div className={`mt-8 flex w-full gap-[1px]`}>
        {normalized.map((item, i) => (
          <div
            key={item.name}
            style={{
              width: `${item.normalized}%`,
              backgroundColor: item.name === REST_LABEL ? `var(--fg-strong)` : COLORS[i],
            }}
            className={`h-10 rounded-lg`}
          ></div>
        ))}
      </div>
      <div className="divide-border-subtle mt-5 flex w-full flex-col divide-y">
        {normalized
          .filter((row) => row.normalized > 0)
          .map((row, i) =>
            row.name !== REST_LABEL ? (
              <Row
                key={row.name}
                name={row.name}
                value={row.count}
                color={row.name === REST_LABEL ? `var(--fg-strong)` : COLORS[i]}
                share={Math.round((row.count / total) * 100)}
                onClick={() => openModal(row.name)}
              />
            ) : (
              <Stats key={row.name} title="Leads Generated" count={row.count} breakdown={rest} disableStyling>
                <Row
                  key={row.name}
                  name={row.name}
                  value={row.count}
                  color={row.name === REST_LABEL ? `var(--fg-strong)` : COLORS[i]}
                  share={Math.round((row.count / total) * 100)}
                  onClick={() => openModal(row.name)}
                />
              </Stats>
            ),
          )}
      </div>
    </div>
  )
}

function Row({
  name,
  value,
  color,
  share,
  onClick,
}: {
  name: string
  value: number
  color: string
  share: number
  onClick: () => void
}) {
  return (
    <div
      className="grid w-full cursor-pointer grid-cols-[3rem_1fr_minmax(75px,auto)_minmax(86px,auto)] items-center"
      onClick={() => onClick()}
    >
      <div className="flex aspect-square h-12 items-center justify-center">
        <div style={{ backgroundColor: color }} className="h-3 w-3 rounded-[3px]"></div>
      </div>
      <div className="text-strong min-w-0 truncate text-label-sm">{name}</div>
      <div className="text-default overflow-hidden text-ellipsis px-4 text-right text-label-sm">
        {value.toLocaleString(`en-US`)}
      </div>
      <div className="overflow-hidden text-ellipsis px-4 text-right text-label-sm text-subtle">({share}%)</div>
    </div>
  )
}
